import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteUser, getUsers } from "../services/UserAPI";
import { FiLogOut, FiEdit, FiTrash2 } from 'react-icons/fi';
import { uploadFile} from "../services/ContactAPI";
import * as XLSX from "xlsx";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);
  const getAllUsers = async () => {
    const data = await getUsers();
    setUsers(data);
  }

  useEffect(() => {
    getAllUsers();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      //console.log("id", id);
      await deleteUser({ _id: id });
      getAllUsers();
    } catch (err) {
      alert("Error deleting user.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  const handleChange = (e) => {
    setFile(e.target.files[0]);
    if (e.target.files[0]) {
      handleUpload(e.target.files[0]);
    }
  };
  const handleUpload = async (selectedFile) => {
    if (!selectedFile) return alert('Please select a file');
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const res = await uploadFile(formData);
      setUploadResponse(res);
      if(res?.success === true){
        alert("Contacts uploaded successfully");
      } else {
        alert(res.message);
      }
      return res;
    } catch (err) {
      console.error(err);
    }
  };
  const downloadSkippedAsXLSX = (skippedArray) => {
    if (!skippedArray || skippedArray.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(skippedArray);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Skipped Records");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "skipped_records.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className=" mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <h1 className="text-xl font-semibold text-gray-900">Users</h1>
            <nav className="flex space-x-8">
              <a
                href="/home"
                className="px-6 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-all duration-200">
                Users
              </a>
              <button onClick={handleLogout} className="text-red-500 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium">
                <FiLogOut />
              </button>
            </nav>
          </div>
        </div>
      </header>
      <main className=" mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <button onClick={() => navigate('/users/add')} className="px-6 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-all duration-200">
            Add User
          </button>
          <div>
            <input
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              id="upload-xlsx-input"
              onChange={handleChange}
            />
            <button
              className="px-6 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 transition-all duration-200 ml-4"
              onClick={() => document.getElementById('upload-xlsx-input').click()}
            >
              Upload Contacts (XLSX)
            </button>
            {uploadResponse?.skippedCount > 0 && (
              <button
                onClick={() => downloadSkippedAsXLSX(uploadResponse.skipped)}
                className="px-4 py-2 ml-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors mt-4"
              >
                Download Skipped Records ({uploadResponse.skippedCount})
              </button>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-800 uppercase tracking-wider">No</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-800 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-800 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-800 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-gray-800 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user, index) => (
                  <tr key={user._id || user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-md text-gray-700">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-md font-medium text-gray-900">{user.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-md text-gray-800">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex text-md leading-5 font-semibold ${user.role === "super admin"
                        ? "text-blue-800"
                        : user.role === "admin"
                          ? "text-blue-800"
                          : "text-green-800"
                        }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/users/edit/${user._id || user.id}`)}
                        className="bg-blue-50 px-2 py-1 rounded-md text-indigo-600 hover:text-indigo-900 mr-4">
                        <FiEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(user._id || user.id)}
                        className="text-red-600 hover:text-red-900">
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="p-4 text-center text-gray-500">No users found.</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserManagement;